import _ from 'lodash';
import { ProductCostPrice } from '@/model/product-cost-price';
import { WeightBracketPrice } from '@/model/weight-bracket-price';
import { ProductSalePrice } from '@/model/product-sale-price';
import { ReducedSalePriceRow, SalePriceColumn, SalePriceRow } from '@/store/proposal/proposal.module';
import { WeightBracketSalePrice } from '@/model/weight-bracket-sale-price';
import { NewProposalProductList } from '@/model/request/new-proposal.request';
import { UpdateProductList } from '@/model/request/update-proposal.request';
import { ProposalStatus } from '@/model/proposal-status';
import { SupplierParams, SuppliersSection } from '@/model/suppliers-choices';
import { Disclaimer } from '@/model/proposal-content';

type Productable = ProductCostPrice | ProductSalePrice;

/**
 * Two prices covering the same weight bracket within one zone group is bad data: it means rows
 * differing only by zone arrived without their zone. Keep the first and record the rest, rather
 * than letting array order silently decide. On correct data there is one match per bracket.
 */
let bracketCollisions: string[] = [];

function assignBracket(
    carry: Map<number, SalePriceColumn>,
    bracket: number,
    column: SalePriceColumn,
    label: string
): void {
    if (carry.has(bracket)) {
        const conflict = `${label} @ ${bracket}g`;
        if (!bracketCollisions.includes(conflict)) {
            bracketCollisions.push(conflict);
        }
        return;
    }

    carry.set(bracket, column);
}

function rowLabelFor(product: Productable, zone?: string): string {
    return [product.supplier, product.name, product.country?.name, zone]
        .filter(Boolean)
        .join(' / ');
}

const proposalsMapper = {
    /** Returns the collisions seen since the last call, and clears them. */
    takeBracketCollisions(): string[] {
        const collisions = bracketCollisions;
        bracketCollisions = [];
        return collisions;
    },

    mapCostPriceBrackets(products: ProductCostPrice[]): Set<number> {
        return new Set(
            products
                .reduce((carry: number[], product: ProductCostPrice) => [...carry, ...product.costPrices.map((costPrice: WeightBracketPrice) => costPrice.weightMax)], [])
                .sort((a: number, b: number) => a - b)
        );
    },
    mapSalePriceBrackets(products: Productable[]): Set<number> {
        return new Set(
            products
                .reduce((carry: number[], product: Productable) => {
                    if ('new' in product && product?.new === true) {
                        return [ ...carry, ...product.costPrices.map((costPrice: WeightBracketPrice) => costPrice.weightMax) ];
                    }
                    return [ ...carry, ...(product.salePrices?.map((costPrice: WeightBracketPrice) => costPrice.weightMax) || []) ];
                }, [])
                .sort((a: number, b: number) => a - b)
        );
    },
    mapCostPricesProducts(products: ProductCostPrice[], brackets: Set<number>): SalePriceRow[] {
        return products.flatMap((product: ProductCostPrice): SalePriceRow[] => {
            // Group cost prices by zone
            const pricesByZone: { [zone: string]: WeightBracketPrice[] } = {};

            (product.costPrices as WeightBracketPrice[]).forEach((costPrice: WeightBracketPrice) => {
                const zoneName = costPrice.zone || 'no-zone';
                if (!pricesByZone[zoneName]) {
                    pricesByZone[zoneName] = [];
                }
                pricesByZone[zoneName].push(costPrice);
            });

            const zones = Object.keys(pricesByZone);
            const hasMultipleZones = zones.length > 1 || (zones.length === 1 && zones[0] !== 'no-zone');

            if (hasMultipleZones) {
                // Create separate row for each zone
                return zones.map(zoneName => {
                    let previousBracket = 0;
                    return {
                        id: `${product.id}-${zoneName}`,
                        country: product.country.name,
                        supplier: product.supplier,
                        supplierId: product.supplierId?.toString(),
                        product: product.name,
                        contract: product.contract,
                        service: product.service,
                        zone: zoneName === 'no-zone' ? undefined : zoneName,
                        productId: product.id,
                        weightBrackets: Array.from(brackets).reduce((carry: Map<number, SalePriceColumn>, bracket: number) => {
                            pricesByZone[zoneName].forEach((costPrice: WeightBracketPrice) => {
                                if (bracket > costPrice.weightMin && bracket <= costPrice.weightMax) {
                                    assignBracket(carry, bracket, {
                                        isFirst: 0 === carry.size,
                                        weightBracket: {
                                            priceId: costPrice.priceId,
                                            weightMin: previousBracket,
                                            weightMax: bracket,
                                            price: {
                                                currency: costPrice.price.currency,
                                                amount: costPrice.price.amount
                                            },
                                            costPrice: costPrice.price,
                                            grossMargin: 0,
                                            productId: product.id,
                                            zone: costPrice.zone
                                        }
                                    }, rowLabelFor(product, zoneName === 'no-zone' ? undefined : zoneName));
                                }
                            });

                            previousBracket = bracket;
                            return carry;
                        }, new Map<number, SalePriceColumn>()),
                        selected: false,
                        grossMargin: 0,
                        new: product?.new ?? undefined,
                    };
                });
            } else {
                // Single zone or no zones - return single row
                let previousBracket = 0;
                return [{
                    id: product.id,
                    country: product.country.name,
                    supplier: product.supplier,
                    supplierId: product.supplierId?.toString(),
                    product: product.name,
                    contract: product.contract,
                    service: product.service,
                    zone: zones[0] === 'no-zone' ? undefined : zones[0],
                    productId: product.id,
                    weightBrackets: Array.from(brackets).reduce((carry: Map<number, SalePriceColumn>, bracket: number) => {
                        (product.costPrices as WeightBracketPrice[]).forEach((costPrice: WeightBracketPrice) => {
                            if (bracket > costPrice.weightMin && bracket <= costPrice.weightMax) {
                                assignBracket(carry, bracket, {
                                    isFirst: 0 === carry.size,
                                    weightBracket: {
                                        priceId: costPrice.priceId,
                                        weightMin: previousBracket,
                                        weightMax: bracket,
                                        price: {
                                            currency: costPrice.price.currency,
                                            amount: costPrice.price.amount
                                        },
                                        costPrice: costPrice.price,
                                        grossMargin: 0,
                                        productId: product.id,
                                        zone: costPrice.zone
                                    }
                                }, rowLabelFor(product, zones[0] === 'no-zone' ? undefined : zones[0]));
                            }
                        });

                        previousBracket = bracket;
                        return carry;
                    }, new Map<number, SalePriceColumn>()),
                    selected: false,
                    grossMargin: 0,
                    new: product?.new ?? undefined,
                }];
            }
        });
    },
    mapCostPricesSurcharges(products: ProductCostPrice[]): SalePriceRow[] {
        return products
            .map((product: ProductCostPrice): SalePriceRow => {
                const weightBrackets = new Map<number, SalePriceColumn>();
                weightBrackets.set(0, {
                    isFirst: true,
                    weightBracket: {
                        priceId: product.costPrices[0].priceId,
                        weightMin: product.costPrices[0].weightMin,
                        weightMax: product.costPrices[0].weightMax,
                        price: {
                            currency: product.costPrices[0].price.currency,
                            amount: product.costPrices[0].price.amount
                        },
                        costPrice: product.costPrices[0].price,
                        grossMargin: 0,
                        productId: product.id
                    }
                });

                return {
                    id: product.id,
                    country: product.country.name,
                    supplier: product.supplier,
                    supplierId: product.supplierId?.toString(),
                    product: product.name,
                    contract: product.contract,
                    service: product.service,
                    zone: undefined,
                    productId: product.id,
                    description: product.description,
                    weightBrackets,
                    selected: false,
                    grossMargin: 0,
                    new: product?.new ?? undefined,
                }
            })
    },
    mapSalePricesProducts(
        products: ProductSalePrice[],
        brackets: Set<number>,
        proposalStatus: ProposalStatus
    ): SalePriceRow[] {
        return products.flatMap((product: ProductSalePrice): SalePriceRow[] => {
            // Group sale prices by zone
            const pricesByZone: { [zone: string]: WeightBracketSalePrice[] } = {};

            product.salePrices.forEach((salePrice: WeightBracketSalePrice) => {
                const zoneName = salePrice.zone || 'no-zone';
                if (!pricesByZone[zoneName]) {
                    pricesByZone[zoneName] = [];
                }
                pricesByZone[zoneName].push(salePrice);
            });

            const zones = Object.keys(pricesByZone);
            const hasMultipleZones = zones.length > 1 || (zones.length === 1 && zones[0] !== 'no-zone');

            if (hasMultipleZones) {
                // Create separate row for each zone
                return zones.map(zoneName => {
                    return {
                        id: `${product.id}-${zoneName}`,
                        country: product.country.name,
                        supplier: product.supplier,
                        supplierId: product.supplierId?.toString(),
                        product: product.name,
                        contract: product.contract,
                        service: product.service,
                        zone: zoneName === 'no-zone' ? undefined : zoneName,
                        productId: product.id,
                        weightBrackets: Array.from(brackets).reduce((carry: Map<number, SalePriceColumn>, bracket: number) => {
                            pricesByZone[zoneName].forEach((salePrice: WeightBracketSalePrice) => {
                                const condition = ProposalStatus.DRAFT === proposalStatus
                                    ? bracket > salePrice.weightMin && bracket <= salePrice.weightMax
                                    : bracket === salePrice.weightMax;

                                if (condition) {
                                    assignBracket(carry, bracket, {
                                        isFirst: 0 === carry.size,
                                        weightBracket: {
                                            priceId: salePrice.priceId,
                                            weightMin: salePrice.weightMin,
                                            weightMax: salePrice.weightMax,
                                            price: {
                                                currency: salePrice.price.currency,
                                                amount: salePrice.price.amount
                                            },
                                            costPrice: salePrice.costPrice,
                                            grossMargin: salePrice.grossMargin,
                                            productId: product.id,
                                            zone: salePrice.zone
                                        }
                                    }, rowLabelFor(product, zoneName === 'no-zone' ? undefined : zoneName));
                                }
                            });

                            return carry;
                        }, new Map<number, SalePriceColumn>()),
                        selected: false,
                        grossMargin: 0,
                    };
                });
            } else {
                // Single zone or no zones - return single row
                return [{
                    id: product.id,
                    country: product.country.name,
                    supplier: product.supplier,
                    supplierId: product.supplierId?.toString(),
                    product: product.name,
                    contract: product.contract,
                    service: product.service,
                    zone: zones[0] === 'no-zone' ? undefined : zones[0],
                    productId: product.id,
                    weightBrackets: Array.from(brackets).reduce((carry: Map<number, SalePriceColumn>, bracket: number) => {
                        product.salePrices.forEach((salePrice: WeightBracketSalePrice) => {
                            const condition = ProposalStatus.DRAFT === proposalStatus
                                ? bracket > salePrice.weightMin && bracket <= salePrice.weightMax
                                : bracket === salePrice.weightMax;

                            if (condition) {
                                assignBracket(carry, bracket, {
                                    isFirst: 0 === carry.size,
                                    weightBracket: {
                                        priceId: salePrice.priceId,
                                        weightMin: salePrice.weightMin,
                                        weightMax: salePrice.weightMax,
                                        price: {
                                            currency: salePrice.price.currency,
                                            amount: salePrice.price.amount
                                        },
                                        costPrice: salePrice.costPrice,
                                        grossMargin: salePrice.grossMargin,
                                        productId: product.id,
                                        zone: salePrice.zone
                                    }
                                }, rowLabelFor(product, zones[0] === 'no-zone' ? undefined : zones[0]));
                            }
                        });

                        return carry;
                    }, new Map<number, SalePriceColumn>()),
                    selected: false,
                    grossMargin: 0,
                }];
            }
        });
    },
    mapSalePricesSurcharges(
        products: ProductSalePrice[]
    ): SalePriceRow[] {
        return products
            .map((product: ProductSalePrice): SalePriceRow => {
                const weightBrackets = new Map<number, SalePriceColumn>();
                weightBrackets.set(0, {
                    isFirst: true,
                    weightBracket: {
                        priceId: product.salePrices[0].priceId,
                        weightMin: product.salePrices[0].weightMin,
                        weightMax: product.salePrices[0].weightMax,
                        price: {
                            currency: product.salePrices[0].price.currency,
                            amount: product.salePrices[0].price.amount
                        },
                        costPrice: product.salePrices[0].costPrice,
                        grossMargin: product.salePrices[0].grossMargin,
                        productId: product.id
                    }
                });
                return {
                    id: product.id,
                    country: product.country.name,
                    supplier: product.supplier,
                    supplierId: product.supplierId?.toString(),
                    product: product.name,
                    contract: product.contract,
                    service: product.service,
                    zone: undefined,
                    productId: product.id,
                    description: product.description,
                    weightBrackets,
                    selected: false,
                    grossMargin: 0
                }
            });
    },
    mapProductListPrices(outbound: SalePriceRow[], returns: SalePriceRow[], surcharges: SalePriceRow[]): NewProposalProductList[] {
        const outboundRows = this.mapGroupedPrices(outbound);
        const returnsRows = this.mapGroupedPrices(returns);
        const surchargesRows = this.mapGroupedPrices(surcharges);

        return [
            ...Object.keys(outboundRows)
                .map(key => ({
                    productId: key,
                    prices: outboundRows[key].prices.map(price => ({
                        costPriceId: price.priceId,
                        grossMargin: price.grossMargin,
                        price: price.price.amount,
                        weightMin: price.weightMin,
                        weightMax: price.weightMax
                    }))
                })),
            ...Object.keys(returnsRows)
                .map(key => ({
                    productId: key,
                    prices: returnsRows[key].prices.map(price => ({
                        costPriceId: price.priceId,
                        grossMargin: price.grossMargin,
                        price: price.price.amount,
                        weightMin: price.weightMin,
                        weightMax: price.weightMax
                    }))
                })),
            ...Object.keys(surchargesRows)
                .map(key => ({
                    productId: key,
                    description: surchargesRows[key].description,
                    prices: surchargesRows[key].prices.map(price => ({
                        costPriceId: price.priceId,
                        grossMargin: price.grossMargin,
                        price: price.price.amount,
                        weightMin: price.weightMin,
                        weightMax: price.weightMax
                    }))
                }))
        ]
    },
    mapGroupedPrices(rows: SalePriceRow[]): { [key: string]: ReducedSalePriceRow } {
        return rows
            .reduce((carry, row: SalePriceRow) => {
                Array.from(row.weightBrackets.values())
                    .map(column => column.weightBracket)
                    .forEach(price => {
                        carry[price.productId] = carry[price.productId] || { prices: [] }
                        carry[price.productId].prices.push(price)
                        if (row.description) {
                            carry[ price.productId ].description = row.description
                        }
                        if (row.new) {
                            carry[ price.productId ].new = row.new
                        }
                    });
                return carry;
            }, Object.create(null) as { [productId: string]: ReducedSalePriceRow })
    },
    mapUpdateProductsListPrices(outbound: SalePriceRow[], returns: SalePriceRow[], surcharges: SalePriceRow[]): UpdateProductList[] {
        const outboundRows = this.mapGroupedPrices(outbound);
        const returnsRows = this.mapGroupedPrices(returns);
        const surchargesRows = this.mapGroupedPrices(surcharges);

        return [
            ...Object.keys(outboundRows)
                .map(key => ({
                    productId: key,
                    prices: outboundRows[key].prices.map(price => {
                        if (outboundRows[key].new || price.new) {
                            return {
                                costPriceId: price.priceId,
                                grossMargin: price.grossMargin,
                                price: price.price.amount,
                                weightMin: price.weightMin,
                                weightMax: price.weightMax
                            }
                        }
                        return {
                            priceId: price.priceId,
                            grossMargin: price.grossMargin,
                            price: price.price.amount,
                        }
                    })
                })),
            ...Object.keys(returnsRows)
                .map(key => ({
                    productId: key,
                    prices: returnsRows[key].prices.map(price => {
                        if (returnsRows[key].new || price.new) {
                            return {
                                costPriceId: price.priceId,
                                grossMargin: price.grossMargin,
                                price: price.price.amount,
                                weightMin: price.weightMin,
                                weightMax: price.weightMax
                            }
                        }
                        return {
                            priceId: price.priceId,
                            grossMargin: price.grossMargin,
                            price: price.price.amount,
                        }
                    })
                })),
            ...Object.keys(surchargesRows)
                .map(key => ({
                    productId: key,
                    description: surchargesRows[key].description,
                    prices: surchargesRows[key].prices.map(price => {
                        if (surchargesRows[key].new || price.new) {
                            return {
                                costPriceId: price.priceId,
                                grossMargin: price.grossMargin,
                                price: price.price.amount,
                                weightMin: price.weightMin,
                                weightMax: price.weightMax
                            }
                        }
                        return {
                            priceId: price.priceId,
                            grossMargin: price.grossMargin,
                            price: price.price.amount,
                        }
                    })
                }))
        ]
    },
    mapDisclaimers(products: Productable[], contentMocks: { [p: string]: string }): string {
        return Array.from(
            new Set(
                products
                    .map(product => [product.supplier?.trim(), `${product.supplier?.trim()}|${product.name?.trim()}`])
                    .reduce((carry, element) => [...carry, ...element], [])
            )
        )
            .map(key => {
                const content = contentMocks[key];

                if (!content) {
                    return '';
                }

                const parts = key.split('|');

                return `<h2>${parts[0]}</h2>${content}`;
            })
            .reduce((carry, element) => carry + element, '')
    },
    mapDynamicDisclaimers(disclaimers: Disclaimer[]): string {
        return _.map(
            disclaimers,
            (disclaimer) => {
                return `<h2>${disclaimer.fullName}</h2>${disclaimer.body}`;
            }
        ).join('<br/>');
    },
    mapSuppliersParams(suppliers: SuppliersSection[]) : SupplierParams[] {
        return _.reduce(
            _.map(suppliers, 'params'),
            (preparedParamsArray, params) => {
                const preparedParams = _.omitBy(
                    _.mapValues(
                        params,
                        (values) => {
                            return values?.join(',');
                        }
                    ),
                    _.isEmpty
                ) as SupplierParams;
                if (!_.isEmpty(preparedParams)) {
                    preparedParamsArray.push(preparedParams)
                }
                return preparedParamsArray;
            },
            [] as SupplierParams[]
        )
    },
    // Identity of a price line for Add deduplication: same carrier + product + contract + service +
    // country + zone is "the same line". Built from supplierId (not the display name) so renamed
    // carriers still match.
    rowIdentity(row: SalePriceRow): string {
        return [row.supplierId, row.product, row.contract, row.service, row.country, row.zone ?? '']
            .map(part => (part ?? '').toString())
            .join('|');
    },
    rowLabel(row: SalePriceRow): string {
        return row.country ? `${row.supplier} — ${row.product} (${row.country})` : `${row.supplier} — ${row.product}`;
    },
    // Append-only merge of freshly fetched cost-price products into the existing rows. A product
    // with no matching line is added whole (flagged new); a product that already exists only gets
    // the weight brackets it was missing (each flagged new). Nothing existing is overwritten.
    mergeAddedCostPriceRows(
        existingRows: SalePriceRow[],
        existingBrackets: Set<number>,
        incomingProducts: ProductCostPrice[]
    ): { rows: SalePriceRow[]; brackets: Set<number>; addedRows: number; addedBrackets: number; skipped: string[] } {
        const brackets = new Set<number>(
            [...existingBrackets, ...this.mapCostPriceBrackets(incomingProducts)].sort((a, b) => a - b)
        );
        const incomingRows = this.mapCostPricesProducts(incomingProducts, brackets)
            .map((row): SalePriceRow => ({ ...row, new: true }));

        const rows = [...existingRows];
        const indexByIdentity = new Map<string, number>();
        rows.forEach((row, index) => indexByIdentity.set(this.rowIdentity(row), index));

        let addedRows = 0;
        let addedBrackets = 0;
        const skipped: string[] = [];

        incomingRows.forEach((incoming) => {
            const identity = this.rowIdentity(incoming);
            const existingIndex = indexByIdentity.get(identity);

            if (existingIndex === undefined) {
                rows.push(incoming);
                indexByIdentity.set(identity, rows.length - 1);
                addedRows += 1;
                return;
            }

            const existing = rows[existingIndex];
            let mergedAny = false;
            incoming.weightBrackets.forEach((column, bracket) => {
                if (!existing.weightBrackets.has(bracket)) {
                    existing.weightBrackets.set(bracket, {
                        ...column,
                        // Re-home the bracket onto the existing line's product id (the incoming one
                        // carries the supplier-product id). Without this, save would group it as a
                        // separate brand-new product and create a duplicate partial line.
                        weightBracket: { ...column.weightBracket, productId: existing.productId, new: true, grossMargin: 0 }
                    });
                    addedBrackets += 1;
                    mergedAny = true;
                }
            });

            if (!mergedAny) {
                skipped.push(this.rowLabel(incoming));
            }
        });

        return { rows, brackets, addedRows, addedBrackets, skipped };
    },
    mergeAddedSurchargeRows(
        existingRows: SalePriceRow[],
        incomingProducts: ProductCostPrice[]
    ): { rows: SalePriceRow[]; addedRows: number; skipped: string[] } {
        const incomingRows = this.mapCostPricesSurcharges(incomingProducts)
            .map((row): SalePriceRow => ({ ...row, new: true }));

        const rows = [...existingRows];
        const identities = new Set(rows.map((row) => this.rowIdentity(row)));

        let addedRows = 0;
        const skipped: string[] = [];

        incomingRows.forEach((incoming) => {
            const identity = this.rowIdentity(incoming);
            if (identities.has(identity)) {
                skipped.push(this.rowLabel(incoming));
                return;
            }
            identities.add(identity);
            rows.push(incoming);
            addedRows += 1;
        });

        return { rows, addedRows, skipped };
    },
}

export default proposalsMapper;